<template>
  <v-main>
    <v-container fluid>
      <!-- Filter Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title class="d-flex align-center justify-space-between">
              <h3 class="font-weight-bold">Gift Order History</h3>
              <div class="d-flex" style="gap: 10px;">
                <v-btn
                  color="success"
                  small
                  :loading="exportLoading"
                  @click="exportHistory"
                >
                  <v-icon left small>mdi-file-excel</v-icon>
                  Export
                </v-btn>
                <v-btn
                  color="error"
                  small
                  :loading="rectifyLoading"
                  @click="previewRectify"
                >
                  <v-icon left small>mdi-wrench</v-icon>
                  Rectify Misuse
                </v-btn>
              </div>
            </v-card-title>
            <v-card-text>
              <v-row>
                <!-- Search -->
                <v-col md="5">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search ... "
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Position Filter -->
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.position_filter"
                    :items="positionOptions"
                    label="Position"
                    clearable
                    prepend-inner-icon="mdi-account-hard-hat"
                    @input="(page = 1), getData()"
                    @click:clear="(filter.position_filter = null), (page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Misuse Filter -->
                <v-col sm="4">
                  <v-select
                    solo
                    rounded
                    v-model="filter.misuse_filter"
                    :items="misuseFilterOptions"
                    label="Show"
                    prepend-inner-icon="mdi-filter"
                    @input="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title class="flex-wrap gap-2">
              <v-chip class="mr-2 mb-1" small color="primary" label>
                <v-icon left small>mdi-account-group</v-icon>
                Total Users: {{ count }}
              </v-chip>
              <v-chip class="mr-2 mb-1" small color="error" label text-color="white">
                <v-icon left small>mdi-alert</v-icon>
                Misusers: {{ summary.total_misusers }}
              </v-chip>
              <v-chip class="mr-2 mb-1" small color="deep-orange" label text-color="white">
                <v-icon left small>mdi-gift-off</v-icon>
                Confirmed Misused Pts: {{ summary.total_pre_fix_delivered | numberFormat }}
              </v-chip>
              <v-chip class="mr-2 mb-1" small color="orange" label text-color="white">
                <v-icon left small>mdi-clock-alert</v-icon>
                At-Risk Pts: {{ summary.total_pre_fix_pending | numberFormat }}
              </v-chip>
              <v-chip class="mr-2 mb-1" small color="success" label>
                <v-icon left small>mdi-gift</v-icon>
                Total Orders: {{ summary.total_orders }}
              </v-chip>
            </v-card-title>

            <v-card-text>
              <section v-if="isLoading">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>

              <v-simple-table v-else class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Name</th>
                      <th class="text-left">Position</th>
                      <th class="text-left">Phone</th>
                      <th class="text-left">Earned</th>
                      <th class="text-left">On Hold<br/><small class="blue--text">Pending Orders</small></th>
                      <th class="text-left misuse-col">Free Gifts<br/><small class="error--text">Not Deducted</small></th>
                      <th class="text-left">Balance</th>
                      <th class="text-left">True Balance</th>
                      <th class="text-left">Orders<br/><small class="grey--text">Total / Done / Pending</small></th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>

                  <tbody v-if="filteredHistories.length != 0">
                    <tr
                      v-for="(item, i) in filteredHistories"
                      :key="`history_${i}`"
                      :class="rowClass(item, i)"
                    >
                      <td>{{ serialNoStarting + i + 1 }}</td>

                      <td>
                        <div class="d-flex align-center">
                          <v-icon
                            v-if="item.confirmed_misuse_points > 0"
                            small
                            color="error"
                            class="mr-1"
                            title="Confirmed misuser"
                          >mdi-alert-circle</v-icon>
                          <strong>{{ item.name || "—" }}</strong>
                        </div>
                      </td>

                      <td>
                        <v-chip
                          small
                          :color="item.position === 'DRIVER' ? 'blue' : 'purple'"
                          text-color="white"
                        >
                          <v-avatar left>
                            <v-icon size="15">
                              {{ item.position === "DRIVER" ? "mdi-car" : "mdi-truck" }}
                            </v-icon>
                          </v-avatar>
                          {{ item.position || "—" }}
                        </v-chip>
                      </td>

                      <td>{{ item.phone || "—" }}</td>

                      <!-- Earned Points -->
                      <td>
                        <span class="success--text font-weight-medium">
                          {{ item.total_collected_points | numberFormat }}
                        </span>
                      </td>

                      <!-- On Hold: post-fix pending, already deducted from balance -->
                      <td>
                        <span :class="item.post_fix_pending_deducted > 0 ? 'blue--text font-weight-medium' : 'grey--text'">
                          {{ item.post_fix_pending_deducted | numberFormat }}
                        </span>
                      </td>

                      <!-- Free Gifts: confirmed misuse (pre-fix delivered with no type=7 log) -->
                      <td>
                        <v-chip
                          x-small
                          :color="item.confirmed_misuse_points > 0 ? 'error' : 'grey lighten-2'"
                          :text-color="item.confirmed_misuse_points > 0 ? 'white' : 'grey darken-1'"
                        >
                          <v-icon left x-small v-if="item.confirmed_misuse_points > 0">mdi-alert</v-icon>
                          {{ item.confirmed_misuse_points | numberFormat }}
                        </v-chip>
                      </td>

                      <!-- Current Balance -->
                      <td>
                        <span class="primary--text font-weight-bold">
                          {{ item.current_points | numberFormat }}
                        </span>
                      </td>

                      <!-- True Balance (balance after subtracting free gifts) -->
                      <td>
                        <span :class="item.true_balance < 0 ? 'error--text font-weight-bold' : 'teal--text font-weight-bold'">
                          {{ item.true_balance | numberFormat }}
                        </span>
                      </td>

                      <!-- Orders: Total / Delivered / Pending in one cell -->
                      <td>
                        <span class="blue--text font-weight-medium">{{ item.total_gift_orders }}</span>
                        <span class="grey--text mx-1">/</span>
                        <span class="success--text font-weight-medium">{{ item.delivered_gift_orders }}</span>
                        <span class="grey--text mx-1">/</span>
                        <span class="orange--text font-weight-medium">{{ item.pending_gift_orders }}</span>
                      </td>

                      <td class="text-right">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              class="mx-1"
                              fab
                              x-small
                              color="primary"
                              :to="`/gift-orders?user_id=${encryptIt(item.id)}`"
                            >
                              <v-icon dark>mdi-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>View Gift Orders</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              class="mx-1"
                              fab
                              x-small
                              color="teal"
                              :to="`/point-mapping-logs?user_id=${encryptIt(item.id)}`"
                            >
                              <v-icon dark>mdi-chart-line</v-icon>
                            </v-btn>
                          </template>
                          <span>View Point Logs</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>

                  <tbody v-else>
                    <tr>
                      <td colspan="11" class="text-center">No data found</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div class="d-flex align-center mt-2" style="position: relative;">
                <!-- Rows per page: pinned left -->
                <div class="d-flex align-center" style="gap: 8px; position: absolute; left: 0;">
                  <span class="text-body-2 grey--text">Rows per page:</span>
                  <v-select
                    v-model="rowsPerPage"
                    :items="rowsPerPageOptions"
                    dense
                    outlined
                    hide-details
                    style="max-width: 100px;"
                    @change="(page = 1), getData()"
                  ></v-select>
                </div>
                <!-- Pagination: centered -->
                <v-pagination
                  v-if="rowsPerPage !== 'All'"
                  rounded
                  v-model="page"
                  :length="paginationLength"
                  :total-visible="10"
                  class="mx-auto"
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Rectify Misuse Dialog -->
    <v-dialog v-model="rectifyDialog" max-width="960" scrollable>
      <v-card class="rectify-dialog">

        <!-- ── Header ── -->
        <div class="rectify-header px-6 py-4">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center" style="gap: 10px;">
              <div class="rectify-icon-wrap">
                <v-icon color="white" size="22">mdi-wrench-check</v-icon>
              </div>
              <div>
                <div class="text-h6 font-weight-bold white--text">Rectify Misuse</div>
                <div class="text-caption white--text" style="opacity: 0.75;">
                  Preview changes before applying
                </div>
              </div>
            </div>
            <div class="d-flex align-center" style="gap: 8px;">
              <div class="rectify-stat-badge">
                <v-icon x-small color="white" class="mr-1">mdi-account-group</v-icon>
                <span>{{ rectifyPreview.length }} Users</span>
              </div>
              <div class="rectify-stat-badge" style="background: rgba(255,255,255,0.15);">
                <v-icon x-small color="white" class="mr-1">mdi-arrow-down-circle</v-icon>
                <span>{{ rectifyPreview.reduce((s, u) => s + (u.scenario === 'atrisk_pending' ? u.pre_fix_pending_points : u.confirmed_misuse_points), 0) | numberFormat }} pts</span>
              </div>
            </div>
          </div>

          <!-- Scenario legend -->
          <div class="d-flex flex-wrap mt-3" style="gap: 6px;">
            <div class="legend-pill legend-cancel">
              <v-icon x-small class="mr-1">mdi-cancel</v-icon>Cancel Pending + Fix Misuse
            </div>
            <div class="legend-pill legend-orange">
              <v-icon x-small class="mr-1">mdi-minus-circle</v-icon>Fix Misuse (Pending Kept)
            </div>
            <div class="legend-pill legend-grey">
              <v-icon x-small class="mr-1">mdi-check-circle</v-icon>Fix Misuse
            </div>
            <div class="legend-pill legend-atrisk">
              <v-icon x-small class="mr-1">mdi-clock-alert</v-icon>Deduct At-Risk
            </div>
            <div class="legend-pill legend-atrisk-cancel">
              <v-icon x-small class="mr-1">mdi-cancel</v-icon>Cancel At-Risk Orders
            </div>
          </div>
        </div>

        <!-- ── Table ── -->
        <v-card-text class="pa-0" style="max-height: 460px; overflow-y: auto;">
          <table class="rectify-table">
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Action</th>
                <th class="text-right">Deducted</th>
                <th class="text-center">Cancelled</th>
                <th class="text-right">Refunded</th>
                <th class="text-right">Before</th>
                <th class="text-right">After</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(u, i) in rectifyPreview"
                :key="u.id"
                :class="['rectify-row', `rectify-row--${u.scenario}`]"
              >
                <td class="text-center row-num">{{ i + 1 }}</td>
                <td>
                  <div class="font-weight-bold text-body-2">{{ u.name }}</div>
                  <div class="text-caption grey--text">{{ u.phone }}</div>
                </td>
                <td>
                  <span :class="['scenario-badge', `badge--${u.scenario}`]">
                    {{ scenarioLabel(u.scenario) }}
                  </span>
                </td>
                <td class="text-right">
                  <span class="deduct-val">
                    −{{ (u.scenario === 'atrisk_pending' ? u.pre_fix_pending_points : u.confirmed_misuse_points) | numberFormat }}
                  </span>
                </td>
                <td class="text-center">
                  <span v-if="u.pending_cancelled > 0" class="cancel-badge">
                    {{ u.pending_cancelled }}
                  </span>
                  <span v-else class="muted">—</span>
                </td>
                <td class="text-right">
                  <span v-if="u.refunded_points > 0" class="refund-val">
                    +{{ u.refunded_points | numberFormat }}
                  </span>
                  <span v-else class="muted">—</span>
                </td>
                <td class="text-right before-val">{{ u.balance_before | numberFormat }}</td>
                <td class="text-right">
                  <span :class="u.balance_after < 0 ? 'after-val--neg' : 'after-val--pos'">
                    {{ u.balance_after | numberFormat }}
                  </span>
                </td>
              </tr>
              <tr v-if="rectifyPreview.length === 0">
                <td colspan="8" class="text-center py-6 grey--text">No users to rectify</td>
              </tr>
            </tbody>
          </table>
        </v-card-text>

        <!-- ── Footer ── -->
        <div class="rectify-footer px-6 py-3 d-flex align-center justify-end" style="gap: 10px;">
          <v-btn text class="text-capitalize" @click="rectifyDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            elevation="0"
            class="text-uppercase font-weight-bold"
            :loading="rectifyLoading"
            :disabled="rectifyPreview.length === 0"
            @click="confirmRectify"
          >
            <v-icon left small>mdi-check-bold</v-icon>
            Apply Rectification
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "GiftOrderHistoryPage",
  filters: {
    numberFormat(val) {
      if (val === null || val === undefined) return "0";
      return Number(val).toLocaleString();
    },
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
  },
  data() {
    return {
      filter: {
        search_keyword: "",
        position_filter: "",
        misuse_filter: "all",
      },
      positionOptions: [
        { text: "DRIVER", value: "DRIVER" },
        { text: "TRANSPORTER", value: "TRANSPORTER" },
      ],
      misuseFilterOptions: [
        { text: "All Users", value: "all" },
        { text: "Confirmed Misusers (Pre-fix Delivered > 0)", value: "confirmed" },
        { text: "At-Risk (Pre-fix Pending > 0)", value: "atrisk" },
        { text: "Negative True Balance", value: "negative" },
      ],
      histories: [],
      count: 0,
      summary: {
        total_misusers: 0,
        total_pre_fix_delivered: 0,
        total_pre_fix_pending: 0,
        total_orders: 0,
      },
      page: 1,
      rowsPerPage: 10,
      rowsPerPageOptions: [10, 20, 50, 100, 'All'],
      serialNoStarting: 0,
      paginationLength: 0,
      isLoading: false,
      exportLoading: false,
      rectifyDialog: false,
      rectifyLoading: false,
      rectifyPreview: [],
    };
  },
  computed: {
    filteredHistories() {
      return this.histories; // filtering handled by backend
    },
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    rowClass(item, i) {
      return {
        "row-misuser":  item.confirmed_misuse_points > 0,
        "row-atrisk":   item.confirmed_misuse_points === 0 && item.pre_fix_pending_points > 0,
        "table-row-even": i % 2 === 0,
        "table-row-odd":  i % 2 !== 0,
        "table-row-last": this.filteredHistories.length - 1 === i,
      };
    },
    async getData() {
      this.isLoading = true;
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.position_filter = this.filter.position_filter || "";

      const isAll = this.rowsPerPage === 'All';
      const query =
        (isAll ? "" : "page=" + this.page + "&rowsPerPage=" + this.rowsPerPage + "&") +
        "search_keyword=" +
        this.filter.search_keyword +
        "&position_filter=" +
        this.filter.position_filter +
        "&misuse_filter=" +
        (this.filter.misuse_filter || "all");

      await axios
        .get(`place_orders/driver-gift-history?${query}`)
        .then((response) => {
          this.histories = response.data.data;
          this.count = response.data.count;
          this.summary = response.data.summary || this.summary;
          this.serialNoStarting = isAll ? 0 : (this.page - 1) * this.rowsPerPage;
          this.paginationLength = isAll ? 0 : Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
        });

      this.isLoading = false;
    },

    async exportHistory() {
      this.exportLoading = true;
      const query =
        "&search_keyword=" + (this.filter.search_keyword || "") +
        "&position_filter=" + (this.filter.position_filter || "") +
        "&misuse_filter=" + (this.filter.misuse_filter || "all");

      await axios
        .get(`place_orders/driver-gift-history/export?${query}`)
        .then((response) => {
          if (response.data.success) {
            window.open(response.data.data, "_blank");
          }
        })
        .catch((error) => console.log(error));
      this.exportLoading = false;
    },

    scenarioLabel(scenario) {
      const labels = {
        negative_with_pending: 'Cancel Pending + Fix Misuse',
        positive_with_pending: 'Fix Misuse (Pending Kept)',
        no_pending:            'Fix Misuse',
        atrisk_deduct:         'Deduct At-Risk Points',
        atrisk_cancel:         'Cancel At-Risk Orders',
      };
      return labels[scenario] || scenario;
    },

    scenarioColor(scenario) {
      const colors = {
        negative_with_pending: 'error',
        positive_with_pending: 'orange',
        no_pending:            'grey darken-1',
        atrisk_deduct:         'deep-orange',
        atrisk_cancel:         'purple',
      };
      return colors[scenario] || 'grey';
    },

    async previewRectify() {
      this.rectifyLoading = true;
      await axios
        .post("place_orders/rectify-misuse", { dry_run: true })
        .then((response) => {
          this.rectifyPreview = response.data.rectified || [];
          this.rectifyDialog = true;
        })
        .catch((error) => console.log(error));
      this.rectifyLoading = false;
    },

    async confirmRectify() {
      this.rectifyLoading = true;
      await axios
        .post("place_orders/rectify-misuse", { dry_run: false })
        .then(() => {
          this.rectifyDialog = false;
          this.rectifyPreview = [];
          this.getData(); // Refresh the table
        })
        .catch((error) => console.log(error));
      this.rectifyLoading = false;
    },
  },
};
</script>

<style scoped>
/* ── Main table row highlights ─────────────────────────── */
.row-misuser td  { background-color: #ffebee !important; }
.row-misuser:hover td { background-color: #ffcdd2 !important; }
.row-atrisk td   { background-color: #fff8e1 !important; }
.row-atrisk:hover td  { background-color: #ffecb3 !important; }
.misuse-col { background-color: #fafafa; }

/* ── Rectify dialog ─────────────────────────────────────── */
.rectify-dialog { border-radius: 12px !important; overflow: hidden; }

.rectify-header {
  background: linear-gradient(135deg, #c62828 0%, #e53935 100%);
}

.rectify-icon-wrap {
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
}

.rectify-stat-badge {
  background: rgba(255,255,255,0.2);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  display: flex; align-items: center;
}

.legend-pill {
  display: inline-flex; align-items: center;
  font-size: 11px; font-weight: 600;
  padding: 3px 10px; border-radius: 20px; color: #fff;
}
.legend-cancel  { background: rgba(255,255,255,0.25); }
.legend-orange  { background: rgba(255,152,0,0.75); }
.legend-grey    { background: rgba(255,255,255,0.15); }
.legend-atrisk        { background: rgba(255,87,34,0.75); }
.legend-atrisk-cancel { background: rgba(123,31,162,0.75); }

/* ── Rectify table ──────────────────────────────────────── */
.rectify-table {
  width: 100%; border-collapse: collapse;
  font-size: 13px;
}
.rectify-table thead tr {
  background: #f5f5f5;
  border-bottom: 2px solid #e0e0e0;
}
.rectify-table th {
  padding: 10px 14px;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #757575;
  white-space: nowrap;
}
.rectify-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
.rectify-row:last-child td { border-bottom: none; }
.rectify-row:hover td { background-color: #fafafa; }

/* Row accent by scenario */
.rectify-row--negative_with_pending td:first-child {
  border-left: 3px solid #e53935;
}
.rectify-row--positive_with_pending td:first-child {
  border-left: 3px solid #fb8c00;
}
.rectify-row--no_pending td:first-child {
  border-left: 3px solid #9e9e9e;
}
.rectify-row--atrisk_deduct td:first-child {
  border-left: 3px solid #ff5722;
}
.rectify-row--atrisk_cancel td:first-child {
  border-left: 3px solid #7b1fa2;
}

.row-num { color: #bdbdbd; font-size: 12px; font-weight: 600; }

/* Scenario badge */
.scenario-badge {
  display: inline-block;
  font-size: 10px; font-weight: 700;
  padding: 3px 8px; border-radius: 4px;
  white-space: nowrap; color: #fff;
  text-transform: uppercase; letter-spacing: 0.3px;
}
.badge--negative_with_pending { background: #e53935; }
.badge--positive_with_pending { background: #fb8c00; }
.badge--no_pending            { background: #757575; }
.badge--atrisk_deduct         { background: #ff5722; }
.badge--atrisk_cancel         { background: #7b1fa2; }

/* Value cells */
.deduct-val  { color: #e53935; font-weight: 700; }
.refund-val  { color: #00897b; font-weight: 700; }
.before-val  { color: #757575; }
.after-val--pos { color: #00897b; font-weight: 700; }
.after-val--neg { color: #e53935; font-weight: 700; }
.cancel-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 50%;
  background: #fff3e0; color: #fb8c00;
  font-size: 11px; font-weight: 700;
}
.muted { color: #bdbdbd; }

/* ── Footer ─────────────────────────────────────────────── */
.rectify-footer {
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}
</style>
